import React, { ChangeEvent, useContext, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { ContainerNew } from 'presentation/shared/components/ContainerNew'
import { useHistory, useLocation } from 'react-router'
import HeaderNew from 'presentation/shared/components/HeaderNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import AutoCompleteNew from 'presentation/shared/components/AutoCompleteNew'
import { ReactComponent as SearchIcon } from 'presentation/assets/icons/search.svg'
import { useServices } from 'presentation/hooks/use-services'
import {
  AmbulatoryAuthorizationDashboardContext,
  Procedure
} from 'main/factories/pages/doctor/ambulatory-authorization/ambulatory-authorization-dashboard-factory'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import CardNew from 'presentation/shared/components/CardNew'
import DividerNew from 'presentation/shared/components/DividerNew'
import * as S from './styles'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import { validationSchema } from './validationSchema'
import { ProcedureCard } from 'presentation/doctor/components/Cards/AmbulatoryProcedureCard'

type LocationProps = {
  isEditing?: boolean
}

export const AmbulatoryProceduresLayout = () => {
  const history = useHistory()
  const stateLocation = useLocation()?.state as LocationProps
  const ambulatoryService = useServices().ambulatory

  const { dispatch, state } = useContext<any>(
    AmbulatoryAuthorizationDashboardContext
  )
  const { procedures, health_insurance_info } = state

  const [procedureSearchTerm, setProcedureSearchTerm] = useState('')

  const [proceduresList, setProceduresList] = useState<any>([])

  const [selectedProceduresList, setSelectedProceduresList] = useState<
    Procedure[]
  >([])

  const [selectedProcedure, setSelectedProcedure] = useState<any>(null)

  if (!state?.patient?.cpf) {
    history.push('/pedidos-autorizacao-ambulatorial/solicitacao')
  }

  const formik = useFormik({
    initialValues: {
      quantity: ''
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      addProcedure(values)
    }
  })

  function selectProcedure(procedure: any) {
    setProcedureSearchTerm(procedure.name)
    setSelectedProcedure(procedure)
  }

  const clearSelection = () => {
    setProcedureSearchTerm('')
    setSelectedProcedure(null)
  }

  async function getProcedures(searchTerm: string) {
    setProceduresList([])
    try {
      const response = await ambulatoryService.getAmbulatoryProcedures({
        search: searchTerm,
        healthInsuranceCode: health_insurance_info.health_insurance_code,
        pageSize: 5,
        pageNumber: 1
      })

      const mappedProcedureList = response.data.map((item) => {
        return {
          label: item.name,
          value: item.code,
          ...item
        }
      })
      setProceduresList(mappedProcedureList)
    } catch (error: any) {
      toast.error(error?.message)
    }
  }

  function addProcedure(values: any) {
    const isDuplicate = selectedProceduresList.some(
      (procedure: any) =>
        Number(procedure.code) === Number(selectedProcedure.code)
    )

    if (isDuplicate) {
      toast.error('Este procedimento já foi adicionado.')
      return
    }

    if (values?.quantity > 100) {
      toast.error('Não é possível adicionar mais de 100 procedimentos.')
      return
    }

    const list = [...selectedProceduresList]

    list.push({
      name: selectedProcedure.name,
      code: Number(selectedProcedure.code),
      quantity: values.quantity,
      isMain: list.length === 0
    })

    setSelectedProcedure(null)
    setSelectedProceduresList(list)
    setProcedureSearchTerm('')
    formik.resetForm()
  }

  function removeProcedure(code: number) {
    const indexToRemove = selectedProceduresList.findIndex(
      (procedure: Procedure) => procedure.code === code
    )

    const updatedList = selectedProceduresList.filter(
      (procedure: Procedure) => procedure.code !== code
    )

    if (selectedProceduresList[indexToRemove]?.isMain) {
      if (updatedList[indexToRemove]) {
        updatedList[indexToRemove].isMain = true
      } else if (updatedList.length > 0) {
        updatedList[0].isMain = true
      }
    }

    setSelectedProceduresList(updatedList)
  }

  const handleInputQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value)
    formik.setFieldValue('quantity', value)
  }

  const handleInputQuantityBlur = (e: ChangeEvent<HTMLInputElement>) => {
    let value = Number(e.target.value)

    if (value <= 0) {
      value = 1
    }

    formik.setFieldValue('quantity', value)
  }

  const handleQuantityChange = (
    code: number,
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const list = [...selectedProceduresList]
    const newQuantity = Number(e.target.value)

    const newList = list.map((procedure: Procedure) =>
      procedure.code === code
        ? { ...procedure, quantity: newQuantity }
        : procedure
    )
    setSelectedProceduresList(newList)
  }

  const handleQuantityBlur = (
    code: number,
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const list = [...selectedProceduresList]
    let newQuantity = Number(e.target.value)

    if (newQuantity <= 0) {
      newQuantity = 1
    }

    const newList = list.map((procedure: Procedure) =>
      procedure.code === code
        ? { ...procedure, quantity: newQuantity }
        : procedure
    )
    setSelectedProceduresList(newList)
  }

  function handleSubmit() {
    if (selectedProceduresList?.some((item) => Number(item?.quantity) > 100)) {
      toast.error('Não é possível adicionar mais de 100 procedimentos.')
      return
    }

    dispatch({
      type: 'PROCEDURES',
      payload: selectedProceduresList
    })

    stateLocation?.isEditing
      ? history.push('resumo#proc')
      : history.push('cids')
  }

  useEffect(() => {
    if (procedures?.length) {
      setSelectedProceduresList(procedures)
    }
  }, [])

  return (
    <>
      <HeaderNew
        actualPageOnClick={() =>
          stateLocation?.isEditing ? history.push('resumo') : history.goBack()
        }
        actualPageTitle="Solicitar pedido ambulatorial"
      />

      <ContainerNew
        title="Procedimento"
        titleColor="neutral800"
        titleSize="large"
        titleWeight="bold"
        subtitle="Inclua os procedimentos que serão realizados e suas quantidades."
        primaryButton={
          <ButtonNew
            fullWidth
            size="large"
            onClick={handleSubmit}
            disabled={selectedProceduresList.length === 0}
          >
            {stateLocation?.isEditing ? 'Salvar' : 'Próximo'}
          </ButtonNew>
        }
      >
        <S.Wrapper>
          <S.FormWrapper onSubmit={formik.handleSubmit}>
            <AutoCompleteNew
              name="procedure"
              placeholder="Digite o TUSS ou nome"
              onInputChange={setProcedureSearchTerm}
              returnFullSuggestion
              labelOverlapping
              label="Procedimento "
              labelSize="xxsmall"
              iconPosition="insideLeft"
              icon={<SearchIcon />}
              minCharactersSearch={3}
              suggestions={proceduresList || []}
              onSuggestionClick={(procedure) => selectProcedure(procedure)}
              value={procedureSearchTerm}
              onType={getProcedures}
              debounceDelay={800}
              disabled={!!selectedProcedure}
              clear={clearSelection}
              required
              requiredColor="neutral800"
            />

            <TextFieldNew
              labelOverlapping
              label="Quantidade "
              placeholder="Digite ou selecione"
              labelSize="xxsmall"
              type="number"
              name="quantity"
              value={formik.values.quantity || ''}
              onChange={(e) => handleInputQuantityChange(e)}
              onBlur={(e) => handleInputQuantityBlur(e)}
              min={1}
              error={formik.touched.quantity && formik.errors.quantity}
              required
              requiredColor="neutral800"
              inputMode="numeric"
              pattern="[0-9]*"
            />

            <ButtonNew
              type="submit"
              size="large"
              outlined
              disabled={!selectedProcedure || !formik.isValid}
            >
              Incluir procedimento
            </ButtonNew>
          </S.FormWrapper>

          <DividerNew marginTop="24px" marginBottom="24px" />

          <HeadingNew
            as="h6"
            weight="semiBold"
            color="neutral600"
            size="medium"
            style={{ marginBottom: '24px' }}
          >
            Procedimentos incluídos
          </HeadingNew>

          {selectedProceduresList?.length ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                paddingBottom: '24px'
              }}
            >
              {selectedProceduresList.map((procedure: Procedure) => {
                return (
                  <ProcedureCard
                    key={procedure.code}
                    procedure={procedure}
                    handleQuantityChange={handleQuantityChange}
                    handleQuantityBlur={handleQuantityBlur}
                    removeProcedure={removeProcedure}
                  />
                )
              })}
            </div>
          ) : (
            <>
              <CardNew
                padding="24px"
                background="neutral50"
                shadow="none"
                border="neutral200"
              >
                <SupportTextNew
                  color="neutral500"
                  size="small"
                  weight="semiBold"
                >
                  Você ainda não incluiu nenhum procedimento
                </SupportTextNew>
              </CardNew>
            </>
          )}
        </S.Wrapper>
      </ContainerNew>
    </>
  )
}

export default AmbulatoryProceduresLayout
